/**
 * Raster-Bild-Karussell: GSAP Crossfade, Autoplay mit Pause,
 * Pointer-Swipe, Pfeiltasten, Reduced Motion ohne Autoplay.
 */
import { gsap } from 'gsap';

const SWIPE_THRESHOLD = 48;

/**
 * @param {HTMLElement} root
 */
function initOne(root) {
  if (root.dataset.r24SlideshowMounted) {
    return;
  }
  root.dataset.r24SlideshowMounted = '1';

  const slides = [...root.querySelectorAll('[data-r24-slide]')];
  const toolbar = root.querySelector('.r24-asset-slideshow__controls');
  const btn = root.querySelector('.r24-asset-slideshow__toggle');
  const btnPrev = root.querySelector('.r24-asset-slideshow__prev');
  const btnNext = root.querySelector('.r24-asset-slideshow__next');
  const statusEl = root.querySelector('.r24-asset-slideshow__sr-only');

  if (slides.length < 2) {
    return;
  }

  const intervalMs = parseInt(String(root.dataset.interval || '4000'), 10) || 4000;
  const fadeMs = parseInt(String(root.dataset.fadeMs || '650'), 10) || 650;
  const fadeSec = fadeMs / 1000;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    root.dataset.reducedMotion = '1';
  }

  let idx = slides.findIndex((el) => el.classList.contains('is-active'));
  if (idx < 0) {
    idx = 0;
  }

  let playing = !prefersReduced;
  /** @type {ReturnType<typeof setInterval> | null} */
  let tick = null;

  function clearTick() {
    if (tick !== null) {
      clearInterval(tick);
      tick = null;
    }
  }

  function startTick() {
    clearTick();
    if (!playing || prefersReduced) {
      return;
    }
    tick = setInterval(() => {
      goTo(idx + 1, { userGesture: false });
    }, intervalMs);
  }

  function syncVisibility() {
    slides.forEach((el, i) => {
      const on = i === idx;
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
  }

  function announce(i) {
    if (!statusEl) {
      return;
    }
    statusEl.textContent = `Bild ${i + 1} von ${slides.length}`;
  }

  /**
   * @param {number} nextIdx
   * @param {{ userGesture?: boolean }} opts
   */
  function goTo(nextIdx, opts = {}) {
    const { userGesture = false } = opts;
    gsap.killTweensOf(slides);

    const prev = idx;
    nextIdx = ((nextIdx % slides.length) + slides.length) % slides.length;

    const prevEl = slides[prev];
    const nextEl = slides[nextIdx];

    prevEl?.classList.remove('is-active');
    nextEl.classList.add('is-active');

    gsap
      .timeline({ overwrite: 'auto' })
      .to(prevEl, { autoAlpha: 0, duration: fadeSec, ease: 'power2.inOut' }, 0)
      .to(nextEl, { autoAlpha: 1, duration: fadeSec, ease: 'power2.inOut' }, 0);

    prevEl?.setAttribute('aria-hidden', 'true');
    nextEl.setAttribute('aria-hidden', 'false');
    idx = nextIdx;
    syncVisibility();

    if (userGesture) {
      announce(idx);
    }

    if (playing && !prefersReduced) {
      clearTick();
      startTick();
    }
  }

  function updateBtn() {
    if (!btn) {
      return;
    }
    const on = playing;
    btn.dataset.playing = on ? 'true' : 'false';
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      on ? 'Automatisches Weiterblenden anhalten' : 'Automatisches Weiterblenden fortsetzen'
    );
  }

  slides.forEach((el, i) => {
    gsap.set(el, { autoAlpha: i === idx ? 1 : 0 });
  });
  syncVisibility();

  if (btn) {
    btn.addEventListener('click', () => {
      playing = !playing;
      updateBtn();
      if (playing && !prefersReduced) {
        startTick();
      } else {
        clearTick();
      }
    });
  }

  btnPrev?.addEventListener('click', () => {
    goTo(idx - 1, { userGesture: true });
  });
  btnNext?.addEventListener('click', () => {
    goTo(idx + 1, { userGesture: true });
  });

  let downX = null;
  root.addEventListener(
    'pointerdown',
    (e) => {
      if (toolbar && e.target instanceof Node && toolbar.contains(e.target)) {
        return;
      }
      if (e.pointerType === 'mouse' && e.button !== 0) {
        return;
      }
      downX = e.clientX;
      try {
        root.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    { passive: true }
  );

  root.addEventListener(
    'pointerup',
    (e) => {
      if (downX === null) {
        return;
      }
      const dx = e.clientX - downX;
      downX = null;
      try {
        root.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (Math.abs(dx) < SWIPE_THRESHOLD) {
        return;
      }
      if (dx < 0) {
        goTo(idx + 1, { userGesture: true });
      } else {
        goTo(idx - 1, { userGesture: true });
      }
    },
    { passive: true }
  );

  root.addEventListener('pointercancel', () => {
    downX = null;
  });

  root.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(idx + 1, { userGesture: true });
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(idx - 1, { userGesture: true });
    }
  });

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (playing && !prefersReduced) {
            startTick();
          }
        } else {
          clearTick();
        }
      });
    },
    { root: null, threshold: 0.08 }
  );
  io.observe(root);

  updateBtn();
  if (playing && !prefersReduced) {
    startTick();
  }
}

export function initAssetSlideshows() {
  document.querySelectorAll('[data-r24-slideshow]').forEach(initOne);
}
